import {
  DocumentRegular,
  LinkRegular,
} from '@fluentui/react-icons';
import { ExcelIcon } from '../Icons/ExcelIcon';
import { PowerPointIcon } from '../Icons/PowerPointIcon';
import { WordIcon } from '../Icons/WordIcon';
import {
  EducationAssignment,
  EducationSubmission,
  EducationSubmissionStatus,
} from '@microsoft/microsoft-graph-types';
import { Course } from '@mnspro-cloud/services';
import { UserRole } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import moment from 'moment-timezone';
import React, { ReactNode } from 'react';
import { Loader } from '../Loader/Loader';
import { AssignmentsBaseModel } from './Assignments.interface';

export const getCourseNameForAssignment = (
  courses: Course[],
  assignment: AssignmentsBaseModel,
): string => {
  return getCourseNameForClassId(courses, assignment.classId);
};

export const getCourseNameForClassId = (courses: Course[], classId: string): string => {
  const course = courses.find((course) => course.id === classId);

  if (course === undefined || !course.displayName) {
    return '';
  }

  return course.displayName;
};

export const textDocumentFileEndings = [
  '.odt',
  '.ott',
  '.oth',
  '.odm',
  '.sxw',
  '.stw',
  '.sxg',
  '.doc',
  '.dot',
  '.doc',
  '.docx',
  '.docm',
  '.dotx',
  '.dotm',
  '.uot',
  '.uof',
];
export const spreadSheetFileEndings = [
  '.ods',
  '.ots',
  '.xls',
  '.xlw',
  '.xlt',
  '.xml',
  '.xlsx',
  '.xlsm',
  '.xltx',
  '.xltm',
  '.xlsb',
  '.uos',
  '.uof',
];
export const presentationFileEndings = [
  '.odp',
  '.otp',
  '.odg',
  '.sxi',
  '.sti',
  '.ppt',
  '.pot',
  '.uop',
  '.pptx',
];

export const isEditableFile = (fileName: string): boolean => {
  const text = textDocumentFileEndings.findIndex((a) => fileName.endsWith(a));
  const spread = spreadSheetFileEndings.findIndex((a) => fileName.endsWith(a));
  const presentation = presentationFileEndings.findIndex((a) => fileName.endsWith(a));

  if (text === -1 && spread === -1 && presentation === -1) {
    return false;
  }
  return true;
};

export const getFileType = (fileName: string): string => {
  if (textDocumentFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return '#microsoft.graph.educationWordResource';
  }

  if (spreadSheetFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return '#microsoft.graph.educationExcelResource';
  }

  if (presentationFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return '#microsoft.graph.educationPowerPointResource';
  }

  if (presentationFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return '#microsoft.graph.educationLinkResource';
  }

  return '#microsoft.graph.educationFileResource';
};

export const getEditableDataType = (fileName: string, isLink: boolean): ReactNode => {
  if (isLink) {
    return <LinkRegular />;
  }
  if (textDocumentFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return <WordIcon />;
  }

  if (spreadSheetFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return <ExcelIcon />;
  }

  if (presentationFileEndings.findIndex((a) => fileName.endsWith(a)) !== -1) {
    return <PowerPointIcon />;
  }

  return <DocumentRegular />;
};

export const assignmentsSearch = (
  assignmentList: EducationAssignment[],
  courses: Course[],
  searchValue: string,
): EducationAssignment[] => {
  const assignments = assignmentList.map<SearchAssignmentsListItem>((a) => ({
    assignmentName: a.displayName ?? '',
    className: getCourseNameForAssignment(courses, {
      classId: a.classId ?? '',
      displayName: a.displayName ?? '',
      dueDateTime: a.dueDateTime ?? '',
      id: a.id ?? '',
    }),
    id: a.id ?? '',
    duedate: moment(a?.dueDateTime).format('LLL'),
  }));
  const searchValueLower = searchValue.toLowerCase();
  const searchedAssignments = assignments.filter(
    (a) =>
      a.assignmentName.toLowerCase().includes(searchValueLower) ||
      a.className.toLowerCase().includes(searchValueLower) ||
      a.duedate.toLowerCase().includes(searchValueLower),
  );

  return assignmentList.filter(
    (a) => searchedAssignments.findIndex((searchedItem) => searchedItem.id === a.id) > -1,
  );
};

export interface SearchAssignmentsListItem {
  assignmentName: string;
  duedate: string;
  className: string;
  id: string;
}

/**
 * Loading indicator used when loading the assignment record
 * data in the detail views.
 */
export const AssignmentRecordLoader = () => {
  return (
    <div className="flex items-center justify-center" style={{ height: '100%' }}>
      <div className="flex-1">
        <Loader centered />
      </div>
    </div>
  );
};

export const filterSubmissionsByStatus = (
  submissions: EducationSubmission[] | null | undefined,
  status: EducationSubmissionStatus,
) => {
  if (!submissions) {
    return [];
  }

  return submissions.filter((submission) => submission.status === status);
};

export const excludeSubmissionsByStatus = (
  submissions: EducationSubmission[] | null | undefined,
  status: EducationSubmissionStatus,
) => {
  if (!submissions) {
    return [];
  }

  return submissions.filter((submission) => submission.status !== status);
};

export const isSubmitted = (submission: EducationSubmission) => {
  const { submittedDateTime, unsubmittedDateTime, reassignedDateTime } = submission;

  if (!submittedDateTime) {
    return false;
  }
  const submittedMoment = moment(submittedDateTime);
  const unsubmittedMoment = moment(unsubmittedDateTime);
  const reassignedMoment = moment(reassignedDateTime);
  return (
    (unsubmittedDateTime === null || submittedMoment > unsubmittedMoment) &&
    (reassignedDateTime === null || submittedMoment > reassignedMoment)
  );
};

export const isUnsubmitted = (submission: EducationSubmission) => {
  const { submittedDateTime, unsubmittedDateTime } = submission;

  if (!unsubmittedDateTime) {
    return false;
  }
  const submittedMoment = moment(submittedDateTime);
  const unsubmittedMoment = moment(unsubmittedDateTime);
  return submittedDateTime === null || submittedMoment < unsubmittedMoment;
};

export const isReassigned = (submission: EducationSubmission) => {
  const { submittedDateTime, returnedDateTime, reassignedDateTime } = submission;

  if (!reassignedDateTime) {
    return false;
  }

  if (returnedDateTime && returnedDateTime === reassignedDateTime) {
    return true;
  }
  const submittedMoment = moment(submittedDateTime);
  const returnedMoment = moment(returnedDateTime);
  const reassignedMoment = moment(reassignedDateTime);

  return (
    (returnedDateTime === null || returnedMoment < reassignedMoment) &&
    (submittedDateTime === null || submittedMoment < reassignedMoment)
  );
};

export const filterAssignedAssignments = (assignments: EducationAssignment[], userRole: UserRole) =>
  assignments.filter((a) => {
    const status = a.status as string;
    if (!status) {
      return;
    }

    const submissions = a.submissions;
    if (!submissions) {
      return;
    }

    if (userRole === UserRole.teacher) {
      return (
        (status === 'published' || status === 'assigned' || status === 'scheduled') &&
        (submissions.length === 0 || submissions.some((a) => !isReturned(a) && !isReassigned(a)))
      );
    }

    return (
      (status === 'published' || status === 'assigned' || status === 'scheduled') &&
      submissions.every((a) => (isReassigned(a) && !isReturned(a)) || a.status === 'working')
    );
  });

export const filterDoneAssignments = (assignments: EducationAssignment[], userRole: UserRole) => {
  if (userRole === UserRole.teacher) {
    return assignments.filter(
      (a) =>
        a.status === 'assigned' &&
        a.submissions &&
        a.submissions.every((s) => isReturned(s) || isReassigned(s)),
    );
  }
  return assignments.filter(
    (a) =>
      a.status === 'assigned' &&
      a.submissions &&
      a.submissions.every((s) => isSubmitted(s) || isReturned(s)),
  );
};

export const isReturned = (submission: EducationSubmission) => {
  const { returnedDateTime, reassignedDateTime } = submission;

  if (!returnedDateTime) {
    return false;
  }

  if (reassignedDateTime && returnedDateTime === reassignedDateTime) {
    return false;
  }

  const returnedMoment = moment(returnedDateTime);
  const reassignedMoment = moment(reassignedDateTime);

  return reassignedDateTime === null || returnedMoment > reassignedMoment;
};

export const isSubmitButtonEnabled = (submission: EducationSubmission) => {
  const { submittedDateTime, unsubmittedDateTime, reassignedDateTime, returnedDateTime } =
    submission;

  const submittedMoment = moment(submittedDateTime);
  const unsubmittedMoment = moment(unsubmittedDateTime);
  const reassignedMoment = moment(reassignedDateTime);
  const returnedMoment = moment(returnedDateTime);

  // Scenario 11:
  if (
    returnedDateTime &&
    (submittedDateTime === null || returnedMoment > submittedMoment) &&
    (unsubmittedDateTime === null || returnedMoment > unsubmittedMoment) &&
    (reassignedDateTime === null || returnedMoment > reassignedMoment)
  ) {
    return true;
  }

  // Scenario 1:
  if (submittedDateTime === null && unsubmittedDateTime === null && reassignedDateTime === null) {
    return true;
  }

  // Scenario 2:
  if (submittedDateTime !== null && unsubmittedDateTime === null && reassignedDateTime === null) {
    return false;
  }

  // Scenario 3:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime !== null &&
    reassignedDateTime === null &&
    submittedMoment > unsubmittedMoment
  ) {
    return false;
  }

  // Scenario 4:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime !== null &&
    reassignedDateTime === null &&
    submittedMoment < unsubmittedMoment
  ) {
    return true;
  }

  // Scenario 5:
  if (submittedDateTime === null && unsubmittedDateTime === null && reassignedDateTime !== null) {
    return true;
  }

  // Scenario 6:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime === null &&
    reassignedDateTime !== null &&
    reassignedMoment > submittedMoment
  ) {
    return true;
  }

  // Scenario 7:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime === null &&
    reassignedDateTime !== null &&
    reassignedMoment < submittedMoment
  ) {
    return false;
  }

  // Scenario 8:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime !== null &&
    reassignedDateTime !== null &&
    submittedMoment > unsubmittedMoment &&
    reassignedMoment > submittedMoment
  ) {
    return true;
  }

  // Scenario 9:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime !== null &&
    reassignedDateTime !== null &&
    submittedMoment > unsubmittedMoment &&
    reassignedMoment < submittedMoment
  ) {
    return false;
  }

  // Scenario 10:
  if (
    submittedDateTime !== null &&
    unsubmittedDateTime !== null &&
    reassignedDateTime !== null &&
    submittedMoment < unsubmittedMoment &&
    reassignedMoment < submittedMoment
  ) {
    return true;
  }

  // Default
  return true;
};
